import { Refusal, type Client } from '@snapshots/transport';

/** An inbox item as the interface shows it: text the producer rendered at read time, never a path */
export interface Notice {
	id: string;
	product: string;
	kind: string;
	title: string;
	summary: string | null;
	occurred: string;
	read: string | null;
	group: string | null;
	/** The whole matter, on a page of matters: its updates and how many are unread */
	updates: { count: number; unread: number; more?: boolean } | null;
	/** When the matter was resolved */
	resolved: string | null;
}

/** Each product's reach as Beyond Projects answers it; `unavailable` is a product whose items are hidden */
export interface Source {
	product: string;
	state: 'available' | 'unavailable';
}

export interface NoticeSummary {
	available: boolean;
	unread: number | null;
	/** The count stopped at Projects' bound, so it reads "N+" */
	more: boolean;
	sources: Source[];
	/** Why the inbox is unavailable, as the service said it; the shared components say it */
	reason?: string | null;
}

/** What the service reads when the inbox cannot be read: never a count */
interface Unavailable {
	available: false;
	reason: 'not_configured' | 'projects_unavailable' | (string & {});
}

export interface NoticePage {
	available: boolean;
	items: Notice[];
	next: string | null;
	sources: Source[];
	/** Why the inbox is unavailable, as the service said it; the shared components say it */
	reason?: string | null;
	/** Projects answered a page of matters */
	by?: 'matter';
}

/**
 * The person's suite inbox (`beyond-notifications/1`), reached through this product's own service,
 * which relays it to Beyond Projects with the person's session. The shape it answers is the one the
 * shared notification entry and inbox read. It performs no business action: reading, marking and
 * opening change read state only, and an opening re-checks permission before it answers a destination.
 *
 * Projects absent, not configured or failing is `available: false` — notifications are unavailable,
 * never an empty inbox. Projects' `more` and `sources` pass through unchanged: the shared components
 * show a bounded count as "N+" and name a source whose state is `unavailable` as unreachable.
 *
 * The service states why the inbox cannot be read, as `200 {available: false, reason}`. With
 * `not_configured` nothing changes until the service is reconfigured, so this adapter never asks again
 * and answers every later count and page as unavailable itself. With `projects_unavailable` the count
 * is asked again only after `RETRY`, whatever the entry's interval; opening the inbox still asks. A
 * readable answer ends either. Every unavailable answer carries that reason, which the components say (`@beyond-js/ui` 0.7.6); when
 * this service itself does not answer there is no reason, and they say only that notifications are unavailable.
 *
 * A count or a page not read because the session ended answers the last one read (`#kept`), or an
 * empty one: the session is said once by `Session`, never by the inbox.
 */
export class Notices {
	/** How long a count stays unavailable before it is asked again while Projects does not answer */
	static RETRY = 300000;

	#client: Client;
	#locale: () => string;
	#listeners = new Set<() => void>();
	#unavailable: { reason: string; until: number } | null = null;
	/** The last count and pages read, by their query, for a read the ended session kept from being sent */
	#kept = new Map<string, NoticeSummary | NoticePage>();

	constructor(client: Client, locale: () => string) {
		this.#client = client;
		this.#locale = locale;
	}

	/** Called after this person's read state changed through any surface of this interface. @returns A function that stops listening */
	listen(listener: () => void): () => void {
		this.#listeners.add(listener);
		return () => void this.#listeners.delete(listener);
	}

	async #changed<T>(work: Promise<T>): Promise<T> {
		const answer = await work;
		for (const listener of this.#listeners) listener();
		return answer;
	}

	static #unreachable(cause: unknown): boolean {
		return cause instanceof Refusal && cause.unreachable;
	}

	static #quiet(cause: unknown): boolean {
		return cause instanceof Refusal && cause.quiet;
	}

	#keep<T extends NoticeSummary | NoticePage>(key: string, answer: T): T {
		this.#kept.set(key, answer);
		return answer;
	}

	/** Why the inbox was last unavailable, or null while it is readable */
	get reason(): string | null {
		return this.#unavailable?.reason ?? null;
	}

	/** Whether the last unavailable answer still stands: always for `not_configured`, for a count until `RETRY` has passed */
	#held(count: boolean): boolean {
		const held = this.#unavailable;
		if (!held) return false;
		return held.reason === 'not_configured' || (count && Date.now() < held.until);
	}

	/** Records what the service answered: a reason is kept, a readable answer clears it */
	#note<T extends object>(answer: T | Unavailable): answer is Unavailable {
		if (!('available' in answer) || answer.available !== false) {
			this.#unavailable = null;
			return false;
		}
		this.#unavailable = { reason: String(answer.reason ?? 'projects_unavailable'), until: Date.now() + Notices.RETRY };
		return true;
	}

	/** Summary counts stop at Projects' bound (99); `more` says the count stopped there */
	async summary(): Promise<NoticeSummary> {
		const unavailable: NoticeSummary = { available: false, unread: null, more: false, sources: [] };
		if (this.#held(true)) return { ...unavailable, reason: this.#unavailable?.reason ?? null };
		try {
			const answer = await this.#client.get<{ unread: number; more: boolean; sources: Source[] } | Unavailable>(`/v1/notifications/summary?locale=${this.#locale()}`);
			if (this.#note(answer)) return { ...unavailable, reason: this.#unavailable?.reason ?? null };
			return this.#keep('summary', { available: true, unread: answer.unread, more: answer.more === true, sources: answer.sources ?? [] });
		} catch (cause) {
			if (Notices.#unreachable(cause)) return unavailable;
			if (Notices.#quiet(cause)) return (this.#kept.get('summary') as NoticeSummary | undefined) ?? { available: true, unread: 0, more: false, sources: [] };
			throw cause;
		}
	}

	async list({ state, product, cursor, limit, by }: { state: 'unread' | 'all'; product: string | null; cursor: unknown; limit: number; by?: 'matter' }): Promise<NoticePage> {
		const query = new URLSearchParams({ state, limit: String(limit), locale: this.#locale() });
		// A page of matters, when the inbox asks one (@beyond-js/ui 0.15.0); a Projects that ignores it answers items
		if (by) query.set('by', by);
		if (product) query.set('product', product);
		if (typeof cursor === 'string' && cursor) query.set('cursor', cursor);
		const unavailable: NoticePage = { available: false, items: [], next: null, sources: [] };
		if (this.#held(false)) return { ...unavailable, reason: this.#unavailable?.reason ?? null };
		try {
			const answer = await this.#client.get<{ items: any[]; next?: string; sources: Source[] } | Unavailable>(`/v1/notifications?${query}`);
			if (this.#note(answer)) return { ...unavailable, reason: this.#unavailable?.reason ?? null };
			const items = answer.items.map(item => ({ id: item.id, product: item.product, kind: item.kind, title: item.title, summary: item.summary ?? null, occurred: item.occurred, read: item.read ?? null, group: item.group ?? null, updates: item.updates ?? null, resolved: item.resolved ?? null }));
			return this.#keep(`${query}`, { available: true, items, next: answer.next ?? null, sources: answer.sources ?? [], ...((answer as { by?: string }).by === 'matter' ? { by: 'matter' as const } : {}) });
		} catch (cause) {
			if (Notices.#unreachable(cause)) return unavailable;
			if (Notices.#quiet(cause)) return (this.#kept.get(`${query}`) as NoticePage | undefined) ?? { available: true, items: [], next: null, sources: [] };
			throw cause;
		}
	}

	read(target: string[] | { before: string; product?: string }): Promise<unknown> {
		return this.#changed(this.#client.post('/v1/notifications/read', Array.isArray(target) ? { ids: target } : target));
	}

	unread(ids: string[]): Promise<unknown> {
		return this.#changed(this.#client.post('/v1/notifications/unread', { ids }));
	}

	/** Marks every update of one matter read (`{product, group}`, the family's notifications by matter) */
	readGroup(matter: { product: string; group: string }): Promise<unknown> {
		return this.#changed(this.#client.post('/v1/notifications/read', { product: matter.product, group: matter.group }));
	}

	/** Marks every update of one matter unread */
	unreadGroup(matter: { product: string; group: string }): Promise<unknown> {
		return this.#changed(this.#client.post('/v1/notifications/unread', { product: matter.product, group: matter.group }));
	}

	/** @throws Refusal `NOT_FOUND` when the item is not, or no longer, visible to this person */
	async open(id: string): Promise<{ destination: string | null }> {
		const answer = await this.#changed(this.#client.post<{ destination: string }>(`/v1/notifications/${encodeURIComponent(id)}/open?locale=${this.#locale()}`));
		return { destination: answer?.destination ?? null };
	}
}
